/** @format */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	fetchCircleInvitations,
	acceptCircleInvitation,
	denyCircleInvitation,
} from "../actions/circleInvitationAction";

export interface CircleInvitationItem {
	_id: string;
	circleId: string;
	status: string;
	response: string | null;
	approvalPolicy: string;
	expiresAt: string;
	respondedAt: string | null;
	created_At: string;
	tokenHash: string;
	circle: {
		_id: string;
		circleName: string;
		circleImage: string;
		memberCount: number;
		leader: { firstName: string; lastName: string } | null;
		isBanned: boolean;
		isActive: boolean;
	} | null;
	inviterName: string;
}

export interface CircleInvitationState {
	invitations: CircleInvitationItem[];
	isLoading: boolean;
	isAccepting: boolean;
	isDenying: boolean;
	error: string;
	acceptError: string;
	denyError: string;
}

const initialState: CircleInvitationState = {
	invitations: [],
	isLoading: false,
	isAccepting: false,
	isDenying: false,
	error: "",
	acceptError: "",
	denyError: "",
};

export const circleInvitationSlice = createSlice({
	name: "circleInvitation",
	initialState,
	reducers: {
		clearCircleInvitationErrors(state) {
			state.error = "";
			state.acceptError = "";
			state.denyError = "";
		},
	},

	extraReducers: (builder) => {
		// ── fetchCircleInvitations ───────────────────────────────────
		builder.addCase(fetchCircleInvitations.pending, (state) => {
			state.isLoading = true;
			state.error = "";
		});
		builder.addCase(
			fetchCircleInvitations.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.invitations = action.payload || [];
			},
		);
		builder.addCase(
			fetchCircleInvitations.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error = action.payload || "Failed to fetch invitations";
			},
		);

		// ── acceptCircleInvitation ───────────────────────────────────
		builder.addCase(acceptCircleInvitation.pending, (state) => {
			state.isAccepting = true;
			state.acceptError = "";
		});
		builder.addCase(
			acceptCircleInvitation.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isAccepting = false;
				// Update the invitation in the list with the returned data
				const updatedInvitation = action.payload?.invitation;
				if (updatedInvitation?._id) {
					state.invitations = state.invitations.map((inv) =>
						inv._id === updatedInvitation._id ?
							{
								...inv,
								status: updatedInvitation.status,
								response: updatedInvitation.response,
								respondedAt: updatedInvitation.respondedAt,
							}
						:	inv,
					);
				}
			},
		);
		builder.addCase(
			acceptCircleInvitation.rejected,
			(state, action: PayloadAction<any>) => {
				state.isAccepting = false;
				state.acceptError = action.payload || "Failed to accept invitation";
			},
		);

		// ── denyCircleInvitation ────────────────────────────────────
		builder.addCase(denyCircleInvitation.pending, (state) => {
			state.isDenying = true;
			state.denyError = "";
		});
		builder.addCase(
			denyCircleInvitation.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isDenying = false;
				const updatedInvitation = action.payload?.invitation;
				if (updatedInvitation?._id) {
					state.invitations = state.invitations.map((inv) =>
						inv._id === updatedInvitation._id ?
							{
								...inv,
								status: updatedInvitation.status,
								response: updatedInvitation.response,
								respondedAt: updatedInvitation.respondedAt,
							}
						:	inv,
					);
				}
			},
		);
		builder.addCase(
			denyCircleInvitation.rejected,
			(state, action: PayloadAction<any>) => {
				state.isDenying = false;
				state.denyError =
					(action.payload as string) || "Failed to deny invitation";
			},
		);
	},
});

export const { clearCircleInvitationErrors } = circleInvitationSlice.actions;

export default circleInvitationSlice.reducer;
